/** Pure authenticated reads. Local epochs cancel subscriptions, not backend authority. */
import { useCallback, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BodyProfileView, CharacterState, HomeSnapshot, PetPrivateSummary, SessionState } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { homeMotionPolicy } from "./homeMotionPolicy";
import type { HomeSceneMotionInput } from "./homeSceneMotion";

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** BodyProfileView has no pet_id. Its identity comes from this authenticated,
 * pet-specific request round; an unexpected conflicting runtime id is rejected.
 * Do not use motion.body_revision to manufacture the current body fact. */
export function currentBodyRevision(body: unknown, pet: PetPrivateSummary): number | null {
  if (!record(body) || ("pet_id" in body && body.pet_id !== pet.pet_id) || body.species !== pet.species) return null;
  const revision = body.world_revision;
  if (typeof revision !== "number" || !Number.isSafeInteger(revision) || revision <= 0) return null;
  if (!["quadruped_tailed", "bird"].includes(String(body.body_plan)) ||
      !record(body.parts) || !record(body.world_parts) ||
      ![body.groups, body.pending_statements, body.conflicts, body.notices].every(Array.isArray) ||
      ![body.show_body_diagram, body.can_edit, body.can_confirm].every((v) => typeof v === "boolean") ||
      typeof body.size_class !== "string" || typeof body.size_class_basis !== "string") return null;
  return revision;
}

export function qualifiedHomeRead(pet: PetPrivateSummary, character: CharacterState, body: BodyProfileView, motion: unknown, epoch: string): HomeSceneMotionInput | null {
  const bodyRevision = currentBodyRevision(body, pet);
  if (bodyRevision === null || !record(motion) || motion.generation_method === "fixture") return null;
  try {
    // The frozen policy/manifest performs header, identity, segment, digest and
    // direction checks. The DTO is never relabelled or promoted to a new catalog.
    const policy = homeMotionPolicy({ pet, state: character, bodyRevision, identityGeneration: epoch,
      motion: motion as unknown as HomeSceneMotionInput["motion"], presence: "home", mode: "free", desiredFacing: "front" });
    if (policy.proof !== "metadata" || !policy.motion || !policy.directions.length) return null;
    return { petId: pet.pet_id, referenceVersion: character.active!.reference_version,
      bodyRevision, identityGeneration: epoch, motion: policy.motion };
  } catch {
    // Invalid transport shapes cannot escape into the scene or grant capability.
    return null;
  }
}

/** Reads once per confirmed context / successful home-snapshot fact round.
 * No reader query cache, initialData, placeholderData, automatic retry, focus
 * refetch, reconnection, poller, mutation or generation endpoint. Existing home
 * refreshes bound freshness; this GET does not provide server body push updates. */
export function useHomeMotionReader(snapshot: HomeSnapshot, character: CharacterState | null): HomeSceneMotionInput | null {
  const { pets } = useServices();
  const household = useOptionalCurrentHousehold();
  const client = useQueryClient();
  const cache = client.getQueryCache();
  const subscribe = useCallback((notify: () => void) => cache.subscribe(() => notify()), [cache]);
  const session = useSyncExternalStore(subscribe,
    useCallback(() => client.getQueryState<SessionState>(queryKeys.session), [client]));
  const userId = household?.userId ?? null;
  const petId = snapshot.pet.pet_id;
  const characterQuery = useSyncExternalStore(subscribe,
    useCallback(() => client.getQueryState<CharacterState>(queryKeys.characterFor(userId ?? "-", petId)), [client, userId, petId]));
  const homeQuery = useSyncExternalStore(subscribe,
    useCallback(() => client.getQueryState<HomeSnapshot>(queryKeys.homeFor(userId ?? "-", petId)), [client, userId, petId]));
  const [expiryTick, setExpiryTick] = useState(0);
  const expiresAt = session?.data?.expires_at;
  const expires = expiresAt == null ? null : Date.parse(expiresAt);
  useLayoutEffect(() => {
    if (expires == null || !Number.isFinite(expires)) return;
    const remaining = expires - Date.now();
    if (remaining <= 0) return;
    // One expiry cancellation, not a periodic session or body request.
    const timer = window.setTimeout(() => setExpiryTick((n) => n + 1), Math.min(remaining, 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [expires, expiryTick]);

  const active = character?.active;
  const neutral = active?.assets.find((asset) => asset.pose === "neutral_full");
  const current = env.dataMode === "live" && snapshot.data_origin === "live" &&
    session?.status === "success" && session.fetchStatus === "idle" && !session.isInvalidated &&
    session.data?.authenticated === true && !!userId && session.data.user?.user_id === userId &&
    (expires == null || (Number.isFinite(expires) && expires > Date.now())) &&
    !!household?.household && household.household.home_activated &&
    household.household.home_id === snapshot.home_id &&
    (!snapshot.household || snapshot.household.household_id === household.household.household_id) &&
    household.pet?.pet_id === petId && household.pet.join_step === "moved_in" &&
    !!active && character?.pet_id === petId && active.pet_id === petId && !!neutral?.content_sha256 &&
    (!characterQuery || (characterQuery.status === "success" && !characterQuery.isInvalidated && characterQuery.fetchStatus === "idle" && characterQuery.data === character)) &&
    (!homeQuery || (homeQuery.status === "success" && !homeQuery.isInvalidated && homeQuery.fetchStatus === "idle" && homeQuery.data === snapshot)) &&
    typeof pets.motion === "function" && typeof pets.bodyProfile === "function";
  const contextKey = JSON.stringify([current, userId, household?.household?.household_id,
    household?.household?.home_id, household?.household?.role, household?.pet?.pet_id,
    petId, snapshot.pet.species, snapshot.server_time, snapshot.version,
    session?.dataUpdateCount, session?.dataUpdatedAt, session?.status, session?.fetchStatus, session?.isInvalidated,
    expiresAt, expiryTick, active?.character_set_id, active?.revision, active?.reference_version,
    active?.style_version, neutral?.content_sha256, active?.assets,
    characterQuery?.dataUpdateCount, characterQuery?.status, characterQuery?.fetchStatus, characterQuery?.isInvalidated,
    homeQuery?.dataUpdateCount, homeQuery?.status, homeQuery?.fetchStatus, homeQuery?.isInvalidated]);
  // Object identity makes A -> B -> A a new round even when the string repeats.
  const round = useMemo(() => ({ key: contextKey }), [contextKey, pets]);
  const mountedId = useId();
  const sequence = useRef(0);
  const [answer, setAnswer] = useState<{ round: typeof round; input: HomeSceneMotionInput | null } | null>(null);
  useLayoutEffect(() => {
    setAnswer(null);
    if (!current || !character || !pets.motion || !pets.bodyProfile) return;
    const controller = new AbortController();
    let valid = true;
    const epoch = `home-read:${mountedId}:${++sequence.current}`;
    // Capture the actual selected pet and successful character inputs, never
    // recover identities or reference/body versions from a motion package.
    const pet = snapshot.pet;
    void (async () => {
      try {
        const [body, motion] = await Promise.all([
          pets.bodyProfile!(pet.pet_id, controller.signal),
          pets.motion!(pet.pet_id, controller.signal),
        ]);
        if (!valid || controller.signal.aborted) return;
        setAnswer({ round, input: qualifiedHomeRead(pet, character, body, motion, epoch) });
      } catch {
        if (!valid || controller.signal.aborted) return;
        controller.abort();
        setAnswer({ round, input: null });
      }
    })();
    return () => { valid = false; controller.abort(); };
    // contextKey captures all consumed identity/fact fields. Promise completion
    // does not change it, so error/unknown cannot form a retry loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);
  return current && answer?.round === round ? answer.input : null;
}
